/**
 * `<ContactsListRoute>` — `/w/:slug/contacts`.
 *
 * Per FD § 2.1 and NFR-Design § 1 + § 4. Cursor-paginated infinite scroll
 * driven by `useInfiniteQuery`; optimistic disable/enable via the
 * `optimisticFlipMutation` helper.
 */

import { Button, Tabs, TabsList, TabsTrigger, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Users } from "../components/icons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { PageShell } from "../components/page-shell";
import { CursorPaginationBar, useCursorPager } from "../components/cursor-pagination";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { SearchInput } from "../components/search-input";
import { DeleteContactDialog } from "../components/contacts/DeleteContactDialog";
import { DisableContactDialog } from "../components/contacts/DisableContactDialog";
import { ContactsTable } from "../components/contacts/ContactsTable";
import { useOptimisticFlipMutation } from "../lib/api/optimistic";
import { useContactsClient } from "../lib/api/use-contacts-client";
import type { Contact, ListContactsFilter, Paginated } from "../lib/api/types";

type FilterKey = "active" | "disabled" | "deleted" | "all";

const STATUS_TABS: ReadonlyArray<{ id: ListContactsFilter["status"]; key: FilterKey }> = [
  { id: "any", key: "all" },
  { id: "active", key: "active" },
  { id: "disabled", key: "disabled" },
  { id: "deleted", key: "deleted" },
];

const FILTER_LABELS: Record<
  FilterKey,
  "list.filters.active" | "list.filters.disabled" | "list.filters.deleted" | "list.filters.all"
> = {
  active: "list.filters.active",
  disabled: "list.filters.disabled",
  deleted: "list.filters.deleted",
  all: "list.filters.all",
};

const STATUS_LABELS: Record<
  "active" | "disabled" | "deleted",
  "status.active" | "status.disabled" | "status.deleted"
> = {
  active: "status.active",
  disabled: "status.disabled",
  deleted: "status.deleted",
};

function debouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function ContactsListRoute() {
  const { t } = useTranslation("contacts");
  const navigate = useNavigate();
  const client = useContactsClient();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get("status") as ListContactsFilter["status"]) ?? "any";
  const [search, setSearch] = useState("");
  const debouncedSearch = debouncedValue(search.trim());

  const filter = useMemo<ListContactsFilter>(
    () => ({
      status,
      search: debouncedSearch === "" ? undefined : debouncedSearch,
      limit: 10,
    }),
    [status, debouncedSearch],
  );

  // Reset to the first page whenever the filter/search changes.
  const pager = useCursorPager();
  useEffect(() => {
    pager.reset();
  }, [status, debouncedSearch, pager.reset]);

  // The optimistic mutations patch the exact cached page (key includes the
  // cursor); the prefix ["contacts","list"] is used for broad invalidation.
  const currentListKey = useMemo(
    () => ["contacts", "list", filter, pager.cursor] as const,
    [filter, pager.cursor],
  );

  const query = useQuery({
    queryKey: currentListKey,
    queryFn: () => client.list({ ...filter, cursor: pager.cursor }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const items: Contact[] = query.data?.items ?? [];
  const canNext = Boolean((query.data as Paginated<Contact> | undefined)?.nextCursor);

  // ── Mutations ────────────────────────────────────────────────

  const disableMutation = useOptimisticFlipMutation<
    Contact,
    { contactId: string; reason?: string }
  >({
    queryClient: qc,
    listKey: currentListKey,
    detailKey: (id) => ["contacts", "detail", id],
    toId: (vars) => vars.contactId,
    mutationFn: (vars) => client.disable(vars.contactId, vars.reason),
    optimisticPatch: (c) => ({ ...c, isDisabled: true }),
    successToast: t("disable.toast"),
    errorToast: t("disable.error"),
  });

  const enableMutation = useOptimisticFlipMutation<Contact, string>({
    queryClient: qc,
    listKey: currentListKey,
    detailKey: (id) => ["contacts", "detail", id],
    mutationFn: (contactId) => client.enable(contactId),
    optimisticPatch: (c) => ({ ...c, isDisabled: false }),
    successToast: t("enable.toast"),
    errorToast: t("enable.error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => client.softDelete(id),
    onSuccess: () => {
      toast.success(t("delete.toast"));
      void qc.invalidateQueries({ queryKey: ["contacts", "list"] });
      // Per FD § 2.2: jump to the deleted-status filter so the operator
      // sees their action reflected.
      setSearchParams({ status: "deleted" });
    },
    onError: () => toast.error(t("delete.error")),
  });

  // ── Confirm dialogs ──────────────────────────────────────────

  const [disableTarget, setDisableTarget] = useState<Contact | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);

  // ── Render ───────────────────────────────────────────────────

  const showActions = status !== "deleted";

  return (
    <PageShell
      data-testid="contacts-list-route"
      className="p-0"
      actions={
        <Button
          asChild
          size="sm"
          className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          data-testid="contacts-new"
        >
          <Link to="new">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t("list.new")}
          </Link>
        </Button>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-[18px] py-[18px]">
        <Tabs value={status ?? "any"} onValueChange={(v) => setSearchParams({ status: v })}>
          <TabsList
            className="h-8 gap-0.5 rounded-md border border-border bg-muted p-0.5"
            data-testid="contacts-status-filter"
          >
            {STATUS_TABS.map((tab) => (
              <TabsTrigger
                key={tab.id ?? "active"}
                value={tab.id ?? "active"}
                className="h-6 rounded-[5px] px-2.5 text-[13px] text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                data-testid={`contacts-filter-${tab.id ?? "any"}`}
              >
                {t(FILTER_LABELS[tab.key])}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("list.search")}
          data-testid="contacts-search"
        />
      </div>

      {query.isError ? (
        <div className="px-6 py-12">
          <ErrorState
            message={t("list.error")}
            onRetry={query.refetch}
            data-testid="contacts-error"
          />
        </div>
      ) : items.length === 0 && !query.isPending ? (
        <div className="px-6 py-12">
          <EmptyState
            data-testid="contacts-empty"
            icon={debouncedSearch ? Search : Users}
            title={
              debouncedSearch
                ? t("list.emptySearch.title")
                : status !== "any"
                  ? t("list.emptyFilter.title", {
                      status: t(STATUS_LABELS[status ?? "active"]),
                    })
                  : t("list.empty.title")
            }
            description={
              debouncedSearch
                ? t("list.emptySearch.body")
                : status !== "any"
                  ? t("list.emptyFilter.body")
                  : t("list.empty.body")
            }
            action={
              debouncedSearch ? (
                <Button
                  variant="secondary"
                  className="rounded-xl hover:bg-secondary-hover"
                  onClick={() => setSearch("")}
                >
                  {t("list.emptySearch.cta")}
                </Button>
              ) : status !== "any" ? (
                <Button
                  variant="secondary"
                  className="rounded-xl hover:bg-secondary-hover"
                  onClick={() => setSearchParams({ status: "any" })}
                >
                  {t("list.emptyFilter.cta")}
                </Button>
              ) : (
                <Button
                  asChild
                  variant="secondary"
                  className="gap-1.5 rounded-xl hover:bg-secondary-hover"
                  data-testid="contacts-empty-create"
                >
                  <Link to="new">
                    <Plus />
                    {t("list.empty.cta")}
                  </Link>
                </Button>
              )
            }
          />
        </div>
      ) : (
        <>
          <ContactsTable
            contacts={items}
            isLoading={query.isPending}
            showActions={showActions}
            onRowClick={(c) => navigate(c.id)}
            onRowHover={(c) => {
              void qc.prefetchQuery({
                queryKey: ["contacts", "detail", c.id],
                queryFn: () => client.get(c.id),
                staleTime: 10_000,
              });
            }}
            onView={(c) => navigate(c.id)}
            onEdit={(c) => navigate(c.id)}
            onDisable={(c) => setDisableTarget(c)}
            onEnable={(c) => enableMutation.mutate(c.id)}
            onDelete={(c) => setDeleteTarget(c)}
          />
          <div className="px-6 py-3">
            <CursorPaginationBar
              data-testid="contacts-pagination"
              pageIndex={pager.pageIndex}
              canPrev={pager.canPrev}
              canNext={canNext}
              busy={query.isFetching}
              onPrev={pager.toPrev}
              onNext={() => pager.toNext(query.data?.nextCursor)}
            />
          </div>
        </>
      )}

      {disableTarget ? (
        <DisableContactDialog
          open={disableTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDisableTarget(null);
          }}
          contactName={disableTarget.displayName ?? disableTarget.userId}
          busy={disableMutation.isPending}
          onConfirm={(reason) => {
            disableMutation.mutate(
              { contactId: disableTarget.id, reason },
              { onSettled: () => setDisableTarget(null) },
            );
          }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteContactDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          contactUserId={deleteTarget.userId}
          contactDisplayName={deleteTarget.displayName}
          busy={deleteMutation.isPending}
          onConfirm={() => {
            deleteMutation.mutate(deleteTarget.id, {
              onSettled: () => setDeleteTarget(null),
            });
          }}
        />
      ) : null}
    </PageShell>
  );
}
